import '../scss/main.scss'

gsap.registerPlugin(ScrollTrigger, SplitText)

import Router from './router/router'
import { pageTransition } from './modules/transitions/pageTransitions'

import Scroll from './modules/scroll'
import AnchorLinks from './modules/anchorLinks'
import CustomSelects from './modules/customSelects'
import Lightbox from './modules/lightbox'
import SvgViewer from './modules/svgViewer'
import ArticleModal from './modules/articleModal'
import HeaderVisibility from './modules/headerVisibility'
import HiddenNav from './modules/hiddenNav'
import TextBlocks from './modules/textBlocks'
import ImagesSliders from './modules/imagesSliders'
import PostsCarousels from './modules/postsCarousels'
import ImagesCarouselSections from './modules/imagesCarouselSections'
import Timelines from './modules/timelines'
import Parallax from './modules/parallax'
import Inview from './modules/inview/inview'
import BackgroundMedias from './modules/backgroundMedias'
import ContentCards from './modules/contentCards'
import DynamicImageCards from './modules/dynamicImageCards'
import VideoFullwidths from './modules/videoFullwidths'
import ChartBars from './modules/chartBars'
import TableBlocks from './modules/tableBlocks'

import { win, loading, appSettings } from './context'
import { setWin, setVh, setLoading, setScroller, isTouchDevice, delay, setHeaderHeight, preloadImages, preloadVideos } from './utils'

class App {
	scroll: any = null
	links: HTMLAnchorElement[] = []
	private viewportResizeObserver: ResizeObserver | null = null
	private viewportResizeRaf: number | null = null

	init = async () => {
		// Initial setup
		this.updateViewportState()

		// On page show
		window.addEventListener('pageshow', this.onPageShow)

		// If touch device
		if (isTouchDevice) document.body.classList.add('is-touch-device')
		
		// Set scroller
		setScroller(document.querySelector('[data-scroll-wrapper]'))
		ScrollTrigger.defaults({ scroller: appSettings.scroller })
		
		// Smooth Scroll
		if (appSettings.smoothScroll && !isTouchDevice) {
			this.scroll = new Scroll()
			this.scroll.init()
		}

		HeaderVisibility.init()
		this.bindViewportResize()

		if (!appSettings.pageTransitionEnabled) {
			const fader: HTMLElement | null = document.body.querySelector('.hg-fader')
			if (fader) fader.parentElement?.removeChild(fader)
		}
		
		// Navigation
		if (appSettings.jsNavigation) {
			Router.init({
				cache: !!appSettings.navigationCache,
			});

			document.addEventListener('linkClick', this.onLinkClick)
			document.addEventListener('beforeLeave', this.onBeforeLeave)
			document.addEventListener('newContentLoad', this.onNewContentLoad)
			document.addEventListener('beforeEnter', this.onBeforeEnter)
			document.addEventListener('afterEnter', this.onAfterEnter)

		} else {
			if (appSettings.pageTransitionEnabled) {
				this.setupLinks()
			}
		}

		// Refresh de scroll/ScrollTrigger bajo demanda desde componentes
		// que alteran la altura del documento (p.ej. TextBlock expand/collapse).
		document.addEventListener('hg:refresh-scroll', this.onRefreshScroll)

		// Anchor links
		AnchorLinks.init()
		CustomSelects.init()
		Lightbox.init()
		SvgViewer.init()
		ArticleModal.init()
		HiddenNav.init()
		TextBlocks.init()
		ImagesSliders.init()
		PostsCarousels.init()
		ImagesCarouselSections.init()
		Timelines.init()

		// Parallax
		Parallax.init()

		// Inview
		Inview.prepare()

		// BackgroundMedias
		BackgroundMedias.init()
		ContentCards.init()
		DynamicImageCards.init()
		VideoFullwidths.init()
		ChartBars.init()
		TableBlocks.init()

		await Promise.all([
			preloadImages(document.documentElement as HTMLElement),
			// preloadVideos(document.documentElement as HTMLElement),
		])

		Inview.commitTriggers()
		ScrollTrigger.refresh()

		if (appSettings.pageTransitionEnabled) pageTransition.enter(document)
	}

	setupLinks = (container: Document | Element = document) => {
		this.links = []
		container.querySelectorAll('a').forEach(element => {
			const el = element as HTMLAnchorElement
			if (
				el.origin === window.location.origin
				&&
				!el.matches('[target="_blank"]')
				&&
				!el.matches('[href^="#"]')
				&&
				!el.matches('[href^="mailto:"]')
				&&
				!el.matches('[href^="tel:"]')
				&&
				!el.matches('.no-router')
				&&
				!/\/wp-(admin|login|content|includes|json)\b/.test(el.pathname)
			) {
				this.links.push(el)
			}
		})

		this.links.forEach(link => {
			link.addEventListener('click', (e: MouseEvent) => {
				e.preventDefault()
				const href = link.href
				pageTransition.exit(document, () => {
					window.location.href = href
				})
			})
		})
	}

	onNewContentLoad = async (e: Event) => {
		const ev = e as CustomEvent
		const newContent = ev.detail.container

		if (!appSettings.pageTransitionEnabled) {
			const fader: HTMLElement | null = newContent.querySelector('.hg-fader')
			if (fader) fader.parentElement?.removeChild(fader)
		}

		// Kill scroll triggers
		let Alltrigger = ScrollTrigger.getAll()
		for (let i = 0; i < Alltrigger.length; i++) {
			Alltrigger[i].kill(true)
		}

		// Set new scroller
		setScroller(newContent.querySelector('[data-scroll-wrapper]'))
		ScrollTrigger.defaults({ scroller: appSettings.scroller })

		// Init new content smooth scroller
		if (appSettings.smoothScroll) {
		if (this.scroll && this.scroll.lenis) this.scroll.lenis.destroy()
			this.scroll = new Scroll()
			this.scroll.init()
		}
		
		// Set new content scroll top
		appSettings.scroller!.scrollTo(0, 0)

		HeaderVisibility.update(newContent)

		// Set new content anchor links
		AnchorLinks.update(newContent)
		CustomSelects.update(newContent)
		Lightbox.update(newContent)
		SvgViewer.update(newContent)
		ArticleModal.update(newContent)
		HiddenNav.update(newContent)
		TextBlocks.update(newContent)
		ImagesSliders.update(newContent)
		PostsCarousels.update(newContent)
		ImagesCarouselSections.update(newContent)
		Timelines.update(newContent)

		// Update parallax
		Parallax.update(newContent)

		// Update inview
		Inview.prepare(newContent)
		
		// Update background medias
		BackgroundMedias.update(newContent)
		ContentCards.update(newContent)
		DynamicImageCards.update(newContent)
		VideoFullwidths.update(newContent)
		ChartBars.update(newContent)
		TableBlocks.update(newContent)

		await Promise.all([
			preloadImages(newContent as HTMLElement),
			// preloadVideos(newContent as HTMLElement),
		])

		Inview.commitTriggers()
		ScrollTrigger.refresh()

		if (appSettings.pageTransitionEnabled) {
			pageTransition.enter(newContent, () => {
				document.dispatchEvent(new CustomEvent('afterEnter'))
				document.documentElement.classList.remove('on-transition')
			})
		} else {
			document.dispatchEvent(new CustomEvent('afterEnter'))
		}
	}
  
	onLinkClick = (e: Event) => {
	}

	onBeforeLeave = () => {
	}
	
	onBeforeEnter = (e: Event) => {
	}
	
	onAfterEnter = (e: Event) => {
	}

	onPageShow = (e: PageTransitionEvent) => {
		if (!e.persisted) return
		const fader = document.querySelector('.hg-fader')
		if (!fader) return
		gsap.killTweensOf(fader)
		pageTransition.enter(document)
	}

	updateViewportState = () => {
		setWin()
		setVh()
		setHeaderHeight()
	}

	refreshViewportState = () => {
		setWin()
		setVh()
		HeaderVisibility.refresh()
	}

	bindViewportResize = () => {
		window.addEventListener('resize', this.onResize, { passive: true })
		window.addEventListener('orientationchange', this.onResize, { passive: true })
		window.visualViewport?.addEventListener('resize', this.onResize, { passive: true })
		window.visualViewport?.addEventListener('scroll', this.onResize, { passive: true })
		screen.orientation?.addEventListener('change', this.onResize)
		document.addEventListener('fullscreenchange', this.onResize)

		if ('ResizeObserver' in window) {
			this.viewportResizeObserver = new ResizeObserver(this.onResize)
			this.viewportResizeObserver.observe(document.documentElement)

			const header = document.querySelector<HTMLElement>('.hg-header')
			if (header) this.viewportResizeObserver.observe(header)
		}

		if ('fonts' in document) {
			void document.fonts.ready.then(this.onResize)
		}
	}

	onResize = () => {
		if (this.viewportResizeRaf !== null) return

		this.viewportResizeRaf = requestAnimationFrame(() => {
			this.viewportResizeRaf = null
			this.refreshViewportState()
			BackgroundMedias.refresh()
			if (this.scroll && this.scroll.lenis) this.scroll.lenis.resize()
			ScrollTrigger.refresh()
		})
	}

	onRefreshScroll = () => {
		if (this.scroll && this.scroll.lenis) this.scroll.lenis.resize()
		ScrollTrigger.refresh()
	}
}

const app = new App()

/** DOM listo (no `load`): evita FOUC en inview mientras cargan imágenes/recursos. */
const runApp = () => {
	void app.init()
}
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', runApp, { once: true })
} else {
	runApp()
}

